export type SplitEmailBody = {
  body: string;
  trimmed: string | null;
};

// Matches a real HTML element tag (e.g. <div>, <br/>, <a href="...">), but NOT
// angle-bracketed plain-text URLs like <https://...> that Gmail uses — for
// those nothing valid follows the scheme word (no `>`, space, or `/`)
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^>]*)?\/?>/i;

export const isHtmlEmailBody = (raw: string): boolean =>
  HTML_TAG_REGEX.test(raw);

const QUOTED_LINE_REGEX = /^\s*>/;
const ORIGINAL_MESSAGE_REGEX =
  /^\s*-{2,}\s*(?:original|forwarded)\s+message\s*-{2,}\s*$/i;
const ATTRIBUTION_OPENING_REGEX = /^\s*On\b/;
const ATTRIBUTION_CLOSING_REGEX = /\bwrote:\s*$/;
const HEADER_FROM_REGEX = /^\s*From:\s*\S/;
const HEADER_FOLLOW_UP_REGEX = /^\s*(?:Sent|Date|To|Cc|Subject):/i;
const DIVIDER_REGEX = /^\s*(?:_{4,}|-{4,}|={4,}|\*{4,})\s*$/;

// "On <date>, <name> <email> wrote:" is often wrapped by the sending client
const ATTRIBUTION_MAX_LINES = 3;
const HEADER_LOOKAHEAD_LINES = 4;

const FOOTER_REGEXES = [
  /^\s*(?:to\s+)?unsubscribe\b/i,
  /^\s*you\s+(?:are\s+)?receiv(?:ed|ing)\s+this\b/i,
  /^\s*this\s+(?:is|was)\s+an?\s+(?:automated\s+|automatic\s+)?(?:notification|message|email|e-mail)\b/i,
  /^\s*(?:©|\(c\)|copyright)\s*\d{4}/i,
  /all\s+rights\s+reserved/i,
];

// boilerplate only counts as a footer once the message is mostly behind us
const FOOTER_ZONE_START = 0.6;

const isAttributionAt = (lines: string[], index: number): boolean => {
  if (!ATTRIBUTION_OPENING_REGEX.test(lines[index])) {
    return false;
  }

  return lines
    .slice(index, index + ATTRIBUTION_MAX_LINES)
    .some((line) => ATTRIBUTION_CLOSING_REGEX.test(line));
};

const isForwardedHeaderAt = (lines: string[], index: number): boolean => {
  if (!HEADER_FROM_REGEX.test(lines[index])) {
    return false;
  }

  return lines
    .slice(index + 1, index + 1 + HEADER_LOOKAHEAD_LINES)
    .some((line) => HEADER_FOLLOW_UP_REGEX.test(line));
};

const isQuoteStartAt = (lines: string[], index: number): boolean =>
  QUOTED_LINE_REGEX.test(lines[index]) ||
  ORIGINAL_MESSAGE_REGEX.test(lines[index]) ||
  isAttributionAt(lines, index) ||
  isForwardedHeaderAt(lines, index);

// a divider right above the trimmed part (Outlook's "______") belongs with it
const includeDividerAbove = (lines: string[], index: number): number => {
  let candidate = index - 1;

  while (candidate >= 0 && !lines[candidate].trim()) {
    candidate -= 1;
  }

  return candidate >= 0 && DIVIDER_REGEX.test(lines[candidate])
    ? candidate
    : index;
};

const splitPlainText = (text: string): SplitEmailBody => {
  const lines = text.split("\n");
  const footerZoneStart = Math.floor(lines.length * FOOTER_ZONE_START);

  for (let index = 0; index < lines.length; index += 1) {
    const isTrimmedStart =
      isQuoteStartAt(lines, index) ||
      (index >= footerZoneStart &&
        FOOTER_REGEXES.some((regex) => regex.test(lines[index])));

    if (isTrimmedStart) {
      const start = includeDividerAbove(lines, index);

      return {
        body: lines.slice(0, start).join("\n"),
        trimmed: lines.slice(start).join("\n"),
      };
    }
  }

  return { body: text, trimmed: null };
};

const HTML_QUOTE_SELECTOR = [
  ".gmail_quote",
  ".moz-cite-prefix",
  'blockquote[type="cite"]',
  "#divRplyFwdMsg",
  "#appendonsend",
  "#mail-editor-reference-message-container",
  "hr#stopSpelling",
].join(",");

const splitHtml = (html: string): SplitEmailBody => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const anchor = doc.body.querySelector(HTML_QUOTE_SELECTOR);

  if (!anchor) {
    return { body: html, trimmed: null };
  }

  // the quote is the anchor plus everything after it within the same parent,
  // which covers both self-contained wrappers and Outlook's sibling markers
  const quoteRoot = doc.createElement("div");
  let node: ChildNode | null = anchor;

  while (node) {
    const nextNode: ChildNode | null = node.nextSibling;
    quoteRoot.appendChild(node);
    node = nextNode;
  }

  return { body: doc.body.innerHTML, trimmed: quoteRoot.innerHTML };
};

/**
 * Separates the new content of an email from the part clients hide behind a
 * toggle — the quoted reply history, and in plain text the boilerplate footer.
 * Falls back to the untouched body when there is nothing to split or when
 * splitting would leave no new content at all.
 */
export const splitEmailTrimmedContent = (raw: string): SplitEmailBody => {
  const split = isHtmlEmailBody(raw) ? splitHtml(raw) : splitPlainText(raw);

  if (!split.trimmed || !split.body.trim()) {
    return { body: raw, trimmed: null };
  }

  return split;
};
